import { produce } from "immer";
import { constant, isEqual, times } from "lodash";
import { useDispatch } from "react-redux";
import { type FormEvent, memo, useState } from "react";
import { useAppSelector } from "Data/Redux/Store";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import "Components/ConfigLayout/Camera/MaskSettings/MaskSettings.less";
import { selectMaskSettings, setMaskSettingsAction } from "Data/Redux/Slices/Settings/Camera/Mask";
import { AspectMaintainer, Button, ButtonTypes, InteractiveSvg, type SelectOption, Select, SettingsLayout,
	Switch, Card } from "@clintonelec/react-storybook";
import type { Point } from "@clintonelec/react-storybook";

const colorOptions: SelectOption[] =
	[ "Black", "White", "Gray", "Yellow", "Red", "Blue", "Green" ].map(color => ({
		label: color,
		value: color.toLowerCase()
	}));

const defaultZonePoints = [
	{ x: 760, y: 340 },
	{ x: 1160, y: 340 },
	{ x: 1160, y: 740 },
	{ x: 760, y: 740 }
];

function MaskSettings() {
	const dispatch = useDispatch();
	const maskSettings = useAppSelector(selectMaskSettings);
	const [ localMaskSettings, setLocalMaskSettings ] = useState(maskSettings);
	const [ selectedMask, setSelectedMask ] = useState<number | null>(null);
	const setMaskSettings = () => dispatch(setMaskSettingsAction(localMaskSettings));
	const allZonesEmpty = !localMaskSettings.zones.some(zone => !!zone);

	const handleMaskColorSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const newLocalState = produce(localMaskSettings, draft => {
			draft.maskColor = value;
		});

		setLocalMaskSettings(newLocalState);
	};

	const handleZoneButtonClick = (zoneIndex: number) => () => {
		const newIndex = selectedMask === zoneIndex ? null : zoneIndex;

		setSelectedMask(newIndex);

		if(newIndex !== null && localMaskSettings.zones[ newIndex ]?.length === 0) {
			const newLocalState = produce(localMaskSettings, draft => {
				draft.zones[ newIndex ] = defaultZonePoints;
			});

			setLocalMaskSettings(newLocalState);
		}
	};

	const handlePointsUpdate = (newPoints: Point[]) => {
		if (selectedMask === null) {
			return;
		}

		const newLocalState = produce(localMaskSettings, draft => {
			draft.zones[ selectedMask ] = newPoints;
		});

		setLocalMaskSettings(newLocalState);
	};

	const handleClearActiveZone = () => {
		if (selectedMask === null) {
			return;
		}

		const newLocalState = produce(localMaskSettings, draft => {
			draft.zones[ selectedMask ] = null;
		});

		setLocalMaskSettings(newLocalState);
		setSelectedMask(null);
	};

	const handleClearAllZones = () => {
		const newLocalState = produce(localMaskSettings, draft => {
			draft.zones = times(8, (constant(null)));
		});

		setLocalMaskSettings(newLocalState);
		setSelectedMask(null);
	};

	const handleFormReset = (event: FormEvent) => {
		event.preventDefault();

		setLocalMaskSettings(maskSettings);
		setSelectedMask(null);
	};

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault();

		setMaskSettings();
		setSelectedMask(null);
	};

	const handlePrivacyMaskEnabledChange = (newEnabled: boolean) => {
		const newLocalState = produce(localMaskSettings, draft => {
			draft.maskEnabled = newEnabled;
		});

		setLocalMaskSettings(newLocalState);
		setSelectedMask(null);
	};

	const handleCreateZoneClick = (index: number) => () => {
		setSelectedMask(index);
		setLocalMaskSettings(produce(localMaskSettings, (draft) => {
			draft.zones[ index ] = defaultZonePoints;
		}));
	};

	const renderZoneButtons = () => times(8, zoneIndex => {
		const selectedButton = selectedMask === zoneIndex;

		if (!localMaskSettings?.zones[ zoneIndex ]) {
			return (
				<Button
					key={ zoneIndex }
					className="zone-button"
					disabled={ !localMaskSettings.maskEnabled }
					ghost={ !selectedButton }
					htmlType="button"
					onClick={ handleCreateZoneClick(zoneIndex) }
					type={ ButtonTypes.SECONDARY }
				>
					+
				</Button>
			);
		}

		return (
			<Button
				key={ zoneIndex }
				className="zone-button"
				disabled={ !localMaskSettings.maskEnabled }
				ghost={ !selectedButton }
				htmlType="button"
				onClick={ handleZoneButtonClick(zoneIndex) }
				type={ !selectedButton ? ButtonTypes.SECONDARY : ButtonTypes.TERTIARY }
			>
				{ `Zone ${ zoneIndex + 1 }` }
			</Button>
		);
	});

	const renderInteractivePolygon = (currentZone: Point[] | null, zoneIndex: number) => {
		const active = zoneIndex === selectedMask;
		const handlePointsChange = active ? handlePointsUpdate : undefined;

		if (!currentZone) {
			return null;
		}

		return (
			<InteractiveSvg.Polygon
				color={ localMaskSettings.maskColor }
				key={ `zone-${ zoneIndex }-${ currentZone.length }` }
				active={ active }
				points={ currentZone }
				onChange={ handlePointsChange }
			/>
		);
	};

	const renderInteractiveSvg = () => {
		if(!localMaskSettings.maskEnabled) {
			return null;
		}

		return (
			<InteractiveSvg>
				{ localMaskSettings.zones.map(renderInteractivePolygon) }
			</InteractiveSvg>
		);
	};

	return (
		<SettingsLayout.Form
			className="mask-settings"
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="Privacy Mask">
					<div className="two-column-card-content">
						<div className="left-content">
							<div className="form-row">
								<Switch
									checked={ localMaskSettings.maskEnabled }
									className="mask-switch"
									onChange={ handlePrivacyMaskEnabledChange }
								/>
								<span>Enable Privacy Mask</span>
							</div>
							<div className="camera-container">
								<AspectMaintainer>
									<div className="camera-snapshot">
										{ renderInteractiveSvg() }
									</div>
								</AspectMaintainer>
							</div>
							<div className="controls-container">
								<div className="zone-buttons">
									{ renderZoneButtons() }
								</div>
								<Button
									className="zone-action-button"
									disabled={ !localMaskSettings.maskEnabled || selectedMask === null }
									ghost
									htmlType="button"
									onClick={ handleClearActiveZone }
									type={ ButtonTypes.SECONDARY }
								>
									Clear Active Zone
								</Button>
								<Button
									className="zone-action-button"
									disabled={ !localMaskSettings.maskEnabled || allZonesEmpty }
									type={ ButtonTypes.SECONDARY }
									ghost
									htmlType="button"
									onClick={ handleClearAllZones }
								>
									Clear All Zones
								</Button>
							</div>
						</div>
						<div className="right-content">
							<div className="form-row mask-color">
								<span>Mask Color</span>
								<Select
									allowClear={ false }
									className="form-select"
									onSelect={ handleMaskColorSelect }
									options={ colorOptions }
									value={ localMaskSettings.maskColor }
								/>
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(maskSettings, localMaskSettings) } />
		</SettingsLayout.Form>
	);
}

export default memo (MaskSettings);
